import { EVENT_WINDOW_SLICES, type CityEventCategoryPages } from '@/lib/city-event-slices';
import type { CityListingPages } from '@todo/core/city-listings';
import { prefetchIndexLists } from '@/lib/detail/list-prefetch';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import {
  CITY_EVENT_CATEGORY_PAGES_SEED_KEY,
  CITY_LISTING_PAGES_SEED_KEY,
  LIST_BUCKET,
} from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';

// Shared by app/[lang]/[city]/events and app/[lang]/[city]/tours. Only cities
// with enough listings (MIN_EVENTS_PER_CITY_PAGE / MIN_TOURS_PER_CITY_PAGE)
// are exported — read from the same prefetched list the city guide and the
// hubs bake for their links, so no link can point at a page that wasn't
// exported. Same shape and explicit-`lang` reasoning as
// generateWineCityStaticParams (wine-tours-detail.ts).
async function cityListingParams(
  kind: keyof CityListingPages,
  params: { lang?: string },
): Promise<{ lang: string; city: string }[]> {
  // Already awaited by app/[lang]/_layout.tsx; memoized, so this is free and
  // just makes the dependency explicit.
  await prefetchIndexLists();
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  const pages = getPrefetched<CityListingPages>(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY);
  return (pages?.[kind] ?? []).map((city) => ({ lang: locale, city }));
}

export function generateCityEventsStaticParams({ params }: { params: { lang?: string } }) {
  return cityListingParams('events', params);
}

export function generateCityToursStaticParams({ params }: { params: { lang?: string } }) {
  return cityListingParams('tours', params);
}

// Shared by app/[lang]/[city]/events/[slice]: the three date windows for every
// city with a /[city]/events page, plus its exported category pages — read
// from the same prefetched lists every link to them bakes. `params.city` is
// set when expo-router passes the parent's params; otherwise every city's
// pages are returned (as generateBoatTypeStaticParams does).
export async function generateCityEventSliceStaticParams({
  params,
}: {
  params: { lang?: string; city?: string };
}): Promise<{ lang: string; city: string; slice: string }[]> {
  await prefetchIndexLists();
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  const pages = getPrefetched<CityListingPages>(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY);
  const categoryPages =
    getPrefetched<CityEventCategoryPages>(LIST_BUCKET, CITY_EVENT_CATEGORY_PAGES_SEED_KEY) ?? {};
  return (pages?.events ?? [])
    .filter((city) => params.city == null || city === params.city)
    .flatMap((city) =>
      [...EVENT_WINDOW_SLICES, ...(categoryPages[city] ?? [])].map((slice) => ({
        lang: locale,
        city,
        slice,
      })),
    );
}
